/**
 * Das Passwort verlässt den Browser nicht.
 *
 * Geschickt wird eine Ableitung: PBKDF2 mit SHA-256 über 200 000 Runden,
 * gesalzen mit dem Benutzernamen. Der Server hasht die noch einmal mit
 * bcrypt und legt nur das ab. Damit kennt der Server das Passwort nie — und
 * wer die Datenbank hätte, hätte trotzdem nur einen bcrypt über eine
 * teure Ableitung.
 *
 * Dieselben Werte stehen in backend/backend/auth/passwort.go. Ändert sich
 * hier etwas, gilt kein bestehendes Passwort mehr.
 */

const RUNDEN = 200000;
const LAENGE = 32;
const SALZ_PRAEFIX = "home-server:v1:";

/** Die Ableitung, oder null, wenn der Browser sie nicht rechnen kann.
 *
 *  `crypto.subtle` gibt es nur in einem sicheren Zusammenhang — über https
 *  oder auf localhost. Wer die Seite über eine nackte IP im Heimnetz
 *  aufruft, bekommt hier null; dann geht das Passwort wie früher zum Server,
 *  der dieselbe Ableitung selbst rechnet. Lieber das, als sich nicht
 *  anmelden zu können. */
export async function passwortAbleiten(
  benutzername: string,
  passwort: string,
): Promise<string | null> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) return null;
  try {
    const enc = new TextEncoder();
    const schluessel = await subtle.importKey(
      "raw", enc.encode(passwort), "PBKDF2", false, ["deriveBits"],
    );
    const bits = await subtle.deriveBits(
      {
        name: "PBKDF2",
        hash: "SHA-256",
        salt: enc.encode(SALZ_PRAEFIX + benutzername.trim().toLowerCase()),
        iterations: RUNDEN,
      },
      schluessel,
      LAENGE * 8,
    );
    return base64Roh(new Uint8Array(bits));
  } catch {
    return null;
  }
}

/** Base64 ohne Füllzeichen — so liest es die Gegenseite (Go: RawStdEncoding). */
function base64Roh(b: Uint8Array): string {
  let s = "";
  for (const x of b) s += String.fromCharCode(x);
  return btoa(s).replace(/=+$/, "");
}
